import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import VisibilityIcon from '@material-ui/icons/Visibility';
import EditIcon from '@material-ui/icons/Edit';
import { Box, Button, Typography, Table, TableCell, TableBody, TableRow, TableHead, TableContainer, Dialog, DialogContent, Card, TextField } from '@material-ui/core';
import React, { FC, useEffect, useState } from 'react';
import { Credit } from '../../types/user';
import RemoveItem from '../common/Remove/RemoveItem';
import dataProvider from '../../providers/dataProvider';
import { removeTags } from '../../helpers/inputsValidate';
import PaginationTable from '../common/paginationTable';
import AddEditCredits from './AddEditCredits';
import HeadTable from '../common/sortTable/sortTable';
import DateFilter from './DateFilter';
import { getFieldByTitle } from '../../helpers/sortByTitle';
import { useNotify, useRefresh } from 'react-admin';
import CreditFilter from './CreditFilter';
import { getDate } from '../../helpers/dateHelper';
import "./index.css"
import ViewSpentCredits from './ViewSpentCredits';

interface TitleProps {
    creditsCount: number;
    openAddModal: (modal: string) => void;
    statusFilter: (type: string) => void;
    isShow: boolean;
}

interface Props {
    id: string
    isShow: boolean
}

interface State {
    credits: Array<Credit>;
    total: number;
    page: number;
    perPage: number;
    field: string;
    order: string;
    totalCredits: number;
    type: string;
    selectedCredit: undefined | Credit
}

const CreditTabTitle: FC<TitleProps> = ({ creditsCount, openAddModal, statusFilter, isShow }) => {
    const [state, setState] = useState({
        earned: true,
        spent: false
    })
    const selectEarned = () => {
        statusFilter("EARNED")
        setState({ ...state, earned: true, spent: false })
    }

    const selectSpent = () => {
        statusFilter("SPENT")
        setState({ ...state, earned: false, spent: true })
    }


    return (
        <>
            <Box display="flex" justifyContent="space-between" paddingBottom="14px" alignItems="center">
                <Typography className="base-color">
                    Credit Balance: {creditsCount} Credits
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="flex-end">
                    <Box display="flex" marginRight="8em" justifyContent="" alignItems="center">
                        <Button className={state.earned ? "active-button" : "inactive-button"} onClick={selectEarned}>Earned</Button>
                        <Button className={state.spent ? "active-button" : "inactive-button"} onClick={selectSpent}>Spent</Button>
                    </Box>
                    {!isShow &&
                        <Box>
                            <Button className="user-cancel-button user-modal-buttons" onClick={() => openAddModal("add")}>Add Credits</Button>
                        </Box>}
                </Box>
            </Box>
        </>
    )
}


const CreditTab: FC<Props> = ({ id, isShow }) => {
    const notify = useNotify()
    const refresh = useRefresh()
    const [state, setState] = useState<State>({
        credits: Array<Credit>(),
        total: 0,
        page: 1,
        perPage: 10,
        field: '_id',
        order: 'asc',
        totalCredits: 0,
        type: "EARNED",
        selectedCredit: undefined
    })

    const [modal, setModal] = useState({
        add: false,
        view: false,
        edit: false,
        delete: false
    })

    const [dropdown, setDropdown] = useState({
        date: false,
        description: false,
        status: false
    })

    const [filter, setFilter] = useState({
        searchField: '',
        startDate: new Date("2000-01-18T19:55:00.000+00:00"),
        endDate: new Date("2030-01-18T19:55:00.000+00:00"),
        arrivals: Array<string>(),
        status: Array<string>()
    })

    useEffect(() => {
        getCredits(1, 10, "_id", "asc", "EARNED", filter.startDate, filter.endDate, Array<string>(), Array<string>())
    }, [])

    const getCredits = async (
        page: number,
        perPage: number,
        field: string,
        order: string,
        type: string,
        startDate: Date,
        endDate: Date,
        arrivals: Array<string>,
        status: Array<string>,
    ): Promise<void> => {
        const resource = type === "EARNED" ? "credits" : "credits/spentCredits"
        const result = await dataProvider.getList(resource, {
            filter: { id: id, startDate: startDate, endDate: endDate, arrival: arrivals, status: status },
            pagination: { page: page, perPage: perPage },
            sort: { field: field, order: order }
        }) as any
        setState({
            ...state,
            credits: result.data,
            totalCredits: type === "EARNED" ? result.balance : state.totalCredits,
            total: result.total,
            type: type,
            page: page,
            perPage: perPage,
            field: field,
            order: order
        })
        setFilter({
            ...filter,
            startDate: startDate,
            endDate: endDate,
            arrivals: arrivals,
            status: status,
        })
    }


    const closeModal = (): void => {
        setModal({ add: false, view: false, edit: false, delete: false })
    }

    const openModal = (currentModal: string, credit?: Credit): void => {
        setState({ ...state, selectedCredit: credit })
        setModal({ ...modal, [currentModal]: true })
    }

    const typeFilter = (type: string) => {
        getCredits(
            state.page,
            state.perPage,
            state.field,
            state.order,
            type,
            filter.startDate,
            filter.endDate,
            filter.arrivals,
            filter.status
        )
    }

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
        getCredits(
            newPage + 1,
            state.perPage,
            state.field,
            state.order,
            state.type,
            filter.startDate,
            filter.endDate,
            filter.arrivals,
            filter.status
        )
    }

    const handleLastPage = () => {
        const page = Math.ceil(state.total / state.perPage)
        getCredits(
            page,
            state.perPage,
            state.field,
            state.order,
            state.type,
            filter.startDate,
            filter.endDate,
            filter.arrivals,
            filter.status
        )
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const perPage = parseInt(event.target.value, 10)
        getCredits(
            1,
            perPage,
            state.field,
            state.order,
            state.type,
            filter.startDate,
            filter.endDate,
            filter.arrivals,
            filter.status
        )

    }

    const handleFirstPage = () => {
        getCredits(
            1,
            state.perPage,
            state.field,
            state.order,
            state.type,
            filter.startDate,
            filter.endDate,
            filter.arrivals,
            filter.status
        )
    }

    const handleSort = (direction: string, title: string): void => {
        const field = getFieldByTitle(title)
        getCredits(
            state.page,
            state.perPage,
            field,
            direction,
            state.type,
            filter.startDate,
            filter.endDate,
            filter.arrivals,
            filter.status
        )
    }

    const filterDropdown = (title: string) => {
        const titleArray = title.toLowerCase()
        setDropdown({ ...dropdown, [titleArray]: !dropdown[titleArray] })
    }

    const applyDateFilter = (): void => {
        getCredits(
            state.page,
            state.perPage,
            state.field,
            state.order,
            state.type,
            filter.startDate,
            filter.endDate,
            filter.arrivals,
            filter.status
        )
    }

    const clearDateFilter = (): void => {
        setFilter({ ...filter, startDate: new Date("2000-01-18T19:55:00.000+00:00"), endDate: new Date("2030-01-18T19:55:00.000+00:00") })
    }

    const setStartDate = (value: Date): void => {
        setFilter({ ...filter, startDate: value })
    }

    const setEndDate = (value: Date): void => {
        setFilter({ ...filter, endDate: value })
    }

    const removeCredits = async (): Promise<void> => {
        const id = state.selectedCredit?._id
        const result = await dataProvider.delete("credits", {
            id: id,
        })
        if (result.data) {
            closeModal()
            notify("Credits deleted", "info")
            refresh()
        }
    }

    const applyFilter = (field: string, conditions: Array<string>) => {
        const status = field === "status" ? conditions : filter.status
        const arrivals = field === "arrival" ? conditions : filter.arrivals
        getCredits(1, state.perPage, state.field, state.order, state.type, filter.startDate, filter.endDate, arrivals, status)
    }

    const closeFilter = (e: any) => {
        setDropdown({ date: false, description: false, status: false })
    }

    return (
        <>
            <Box marginTop="0.5em">
                <Card>
                    <Box padding="2em">
                        <CreditTabTitle
                            creditsCount={state.totalCredits / 100}
                            openAddModal={openModal}
                            statusFilter={typeFilter}
                            isShow={isShow}
                        />
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell id="active-table-head">
                                            <HeadTable title="Date" sort={handleSort} filterHandle={filterDropdown} />
                                            {
                                                dropdown.date
                                                &&
                                                <DateFilter
                                                    applyDateFilter={applyDateFilter}
                                                    clearDateFilter={clearDateFilter}
                                                    endDate={filter.endDate}
                                                    startDate={filter.startDate}
                                                    setStartDate={setStartDate}
                                                    setEndDate={setEndDate}
                                                />
                                            }
                                        </TableCell>
                                        <TableCell id="active-table-head">
                                            <HeadTable title="Description" sort={handleSort} filterHandle={state.type === "EARNED" ? filterDropdown : undefined} />
                                            {
                                                dropdown.description &&
                                                <CreditFilter
                                                    firstCheckboxTitle="Referral"
                                                    secondCheckboxTitle="Admin"
                                                    thirdCheckboxTitle="Bought"
                                                    applyFilter={applyFilter}
                                                    field="arrival"
                                                />
                                            }
                                        </TableCell>
                                        <TableCell id="active-table-head">
                                            <HeadTable title="Amount" sort={handleSort} />
                                        </TableCell>
                                        {
                                            state.type === "EARNED" &&
                                            <TableCell id="active-table-head">
                                                <HeadTable title="Status" sort={handleSort} filterHandle={filterDropdown} />
                                                {
                                                    dropdown.status &&
                                                    <CreditFilter
                                                        firstCheckboxTitle="Active"
                                                        secondCheckboxTitle="Expired"
                                                        thirdCheckboxTitle="Debited"
                                                        applyFilter={applyFilter}
                                                        field="status"
                                                    />
                                                }
                                            </TableCell>
                                        }
                                        <TableCell id="active-table-head-actions">
                                            Actions
                                </TableCell>
                                    </TableRow>
                                </TableHead>
                                {state.credits.map((item, i) => {
                                    return (
                                        <TableBody key={i} onClick={closeFilter}>
                                            <TableRow>
                                                <TableCell>
                                                    {getDate(new Date(item.createdDate))}
                                                </TableCell>
                                                <TableCell>
                                                    {item.description ? removeTags(item.description) : `Bought ${item.tickets} tickets`}
                                                </TableCell>
                                                <TableCell>
                                                    {state.type === "EARNED" ? item.amount / 100 : item.spentCredits || 0 / 100}
                                                </TableCell>
                                                {
                                                    state.type === "EARNED" &&
                                                    <TableCell>
                                                        {item.isExpired ? "Expired" : item.status}
                                                    </TableCell>
                                                }
                                                <TableCell id="action-table-cell">
                                                    <div className="actions-table-body">
                                                        <div onClick={() => openModal('view', item)}>
                                                            <VisibilityIcon className="delete-icon" />
                                                        </div>
                                                        {
                                                            !isShow &&
                                                            <>
                                                                {
                                                                    state.type === "EARNED" &&
                                                                    <div onClick={() => openModal('edit', item)}>
                                                                        <EditIcon className="delete-icon" />
                                                                    </div>
                                                                }
                                                                {
                                                                    state.type === "EARNED" &&
                                                                    <div onClick={() => openModal('delete', item)}>
                                                                        <DeleteForeverIcon className="delete-icon" />
                                                                    </div>}
                                                            </>
                                                        }
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    )
                                })}
                            </Table>
                        </TableContainer>
                        <PaginationTable
                            page={state.page}
                            perPage={state.perPage}
                            collectionLength={state.credits.length}
                            total={state.total}
                            handleChangePage={handleChangePage}
                            handleLastPage={handleLastPage}
                            handleChangeRowsPerPage={handleChangeRowsPerPage}
                            handleFirstPage={handleFirstPage}
                            title="Credits"
                        />
                        <Dialog
                            fullWidth
                            open={modal.add}
                            onClose={closeModal}
                            id="credit-dialog"
                        >
                            <DialogContent>
                                <AddEditCredits closeModal={closeModal} id={id} isEdit={false} isView={false} />
                            </DialogContent>
                        </Dialog>
                        <Dialog
                            fullWidth
                            open={modal.delete}
                            onClose={closeModal}
                            id="credit-dialog"
                        >
                            <DialogContent>
                                <RemoveItem closeModal={closeModal} mainText="credits" minorText="" removeUser={removeCredits} />
                            </DialogContent>
                        </Dialog>
                        <Dialog
                            fullWidth
                            open={modal.edit}
                            onClose={closeModal}
                            id="credit-dialog"
                        >
                            <DialogContent>
                                <AddEditCredits closeModal={closeModal} id={id} isEdit={true} credit={state.selectedCredit} isView={false} />
                            </DialogContent>
                        </Dialog>
                        <Dialog
                            fullWidth
                            open={modal.view}
                            onClose={closeModal}
                            id="credit-dialog"
                        >
                            <DialogContent>
                                {
                                    state.type === "EARNED"
                                        ? <AddEditCredits closeModal={closeModal} id={id} credit={state.selectedCredit} isEdit={true} isView={true} />
                                        : <ViewSpentCredits closeModal={closeModal} order={state.selectedCredit} />
                                }
                            </DialogContent>
                        </Dialog>
                    </Box>
                </Card>
            </Box>
        </>
    )
}

export default CreditTab